import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { emailsApi } from "../api/emails";
import { AppLayout } from "../components/AppLayout";
import { MailWorkspace } from "../components/MailWorkspace";
import { EmailDetailPage } from "./EmailDetailPage";
import { useEmailStore } from "../store/emailStore";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export function EmailsPage() {
  const { selectedVetId, setSelectedVet } = useEmailStore();
  const [status, setStatus] = useState("all");
  const [category, setCategory] = useState("all");
  const [priority, setPriority] = useState("all");
  const {
    data: emails = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["emails-list", status, category, priority, selectedVetId],
    queryFn: () =>
      emailsApi.list({
        status: status === "all" ? undefined : status,
        category: category === "all" ? undefined : category,
        priority: priority === "all" ? undefined : priority,
        vet_id: selectedVetId || undefined,
        limit: 200,
      }),
  });
  const filter = (
    label: string,
    value: string,
    change: (value: string) => void,
    options: [string, string][],
  ) => (
    <Select value={value} onValueChange={change}>
      <SelectTrigger aria-label={label} className="w-full">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all">{label}</SelectItem>
        {options.map(([id, name]) => (
          <SelectItem value={id} key={id}>
            {name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
  return (
    <AppLayout breadcrumbs={[{ label: "Archivio" }]} mainClassName="mail-main">
      <MailWorkspace
        title="Archivio"
        loading={isLoading}
        error={!!error}
        onRetry={() => {
          void refetch();
        }}
        selectionKey="email"
        empty="Nessuna email trovata"
        items={emails.map((email) => ({
          id: email.id,
          sender: email.from_email,
          title: email.subject || "Senza oggetto",
          preview: email.summary_ai,
          date: email.received_at,
          priority: email.priority,
          meta: [
            email.direction === "INBOUND" ? "Ricevuta" : "Inviata",
            email.category,
            email.status,
          ]
            .filter(Boolean)
            .join(" · "),
        }))}
        renderDetail={(id) => <EmailDetailPage emailId={id} embedded />}
        filters={
          <>
            {filter("Tutti gli stati", status, setStatus, [
              ["new", "Nuove"],
              ["processed", "Processate"],
              ["auto_replied", "Risposte automatiche"],
              ["routed", "Assegnate"],
            ])}
            {filter("Tutte le categorie", category, setCategory, [
              ["visita", "Visita"],
              ["referto", "Referto"],
              ["richiesta_info", "Richiesta informazioni"],
              ["altro", "Altro"],
            ])}
            {filter("Tutte le priorità", priority, setPriority, [
              ["urgent", "Urgente"],
              ["high", "Alta"],
              ["medium", "Media"],
              ["low", "Bassa"],
            ])}
            {(status !== "all" ||
              category !== "all" ||
              priority !== "all" ||
              selectedVetId) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setStatus("all");
                  setCategory("all");
                  setPriority("all");
                  setSelectedVet(null);
                }}
              >
                Azzera filtri
              </Button>
            )}
          </>
        }
      />
    </AppLayout>
  );
}
